import { formatQuantity, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_FS_BODY,
  BANK_FS_META,
  BANK_FS_READ,
  BANK_TEXT_2,
  Callout,
  GuardedButton,
  Input,
  NumberStepper,
  ProgressBar,
} from "@hakili/ui";
import { AlertTriangle, Info, Lock } from "lucide-react";
import { useState } from "react";

import { fill } from "./dossiers.js";
import { PolicyCard, RuleRow } from "./politique-parts.js";

/**
 * `POLICY-FIRST` Screen A (D-1028-6; D2 `b284f616`, `screens2.js:299` `screenFirstPolicy`, frames 163 to 165 and 171): **the first
 * definition of the bank's credit policy, by its chef, every one of the eight values typed by him.** Hakili gives none: no field opens on
 * a number, and the stepper only appears once a value is committed, to adjust it (« Tapez chaque valeur ; le stepper sert ensuite à
 * l'ajuster »).
 *
 * A typed value is committed when the field is left or on Enter. A value the door would refuse (not a number, zero, above its limit) is
 * said in words next to its field and is **never clamped** (frame 171): the field keeps what he typed, and the save is locked with D2's
 * reason until it is corrected. Rates and ceilings are kept as whole tenths of a point and amounts as whole numbers: no float is
 * compared or written.
 */

/** What the door takes, field for field (`PUT /api/v1/bank/policy`), the same shape as the policy it later reads back. */
export interface FirstPolicyBody {
  readonly rateTolerancePct: string;
  readonly durationMaxMonths: number;
  readonly vehicleAgeMaxYears: number;
  readonly vehicleAgeAtMaturityMaxYears: number;
  readonly taegCeilingPct: string;
  readonly delegationThresholdAmount: number;
  readonly assignableSharePct: number;
  readonly gradeRates: readonly { readonly grade: string; readonly ratePct: string }[];
}

type Key = "A+" | "A" | "B+" | "B" | "tol" | "term" | "taeg" | "thr" | "ageO" | "ageE" | "quot";
type Unit = "pct" | "point" | "mois" | "fcfa" | "ans";

interface Spec {
  readonly key: Key;
  /** The test id and field id suffix. */
  readonly slug: string;
  /** The row's name; a grade row is built from the grid's `Note {note}`. */
  readonly label: string;
  readonly sub?: string;
  readonly unit: Unit;
  /** A rate or a ceiling: the value is whole tenths of a point (« 11,2 » is 112). Otherwise a whole number. */
  readonly tenths: boolean;
  readonly step: number;
  readonly min: number;
  /** The highest value the stepper reaches. */
  readonly max: number;
  /** The highest value that may be typed (D2 `FP_FIELDS`: 60 months, 14 %, 100 %); the door's own ceiling for the rest. */
  readonly typedMax: number;
}

const rate = (key: "A+" | "A" | "B+" | "B"): Spec => ({
  key,
  slug: key.replace("+", "plus"),
  label: fill("bank.politique.noteLigne", { note: key }),
  unit: "pct",
  tenths: true,
  step: 1,
  min: 1,
  max: 999,
  typedMax: 999,
});

const GRADES = ["A+", "A", "B+", "B"] as const;

const FIELDS: Readonly<Record<Key, Spec>> = {
  "A+": rate("A+"),
  A: rate("A"),
  "B+": rate("B+"),
  B: rate("B"),
  tol: {
    key: "tol",
    slug: "tol",
    label: t("bank.politique.premiereTolerance"),
    sub: t("bank.politique.premiereToleranceSous"),
    unit: "point",
    tenths: true,
    step: 1,
    min: 1,
    max: 999,
    typedMax: 999,
  },
  term: {
    key: "term",
    slug: "term",
    label: t("bank.politique.premiereDuree"),
    sub: t("bank.politique.premiereDureeSous"),
    unit: "mois",
    tenths: false,
    step: 6,
    min: 6,
    max: 60,
    typedMax: 60,
  },
  taeg: {
    key: "taeg",
    slug: "taeg",
    label: t("bank.politique.premiereTaeg"),
    sub: t("bank.politique.premiereTaegSous"),
    unit: "pct",
    tenths: true,
    step: 1,
    min: 1,
    max: 140,
    typedMax: 140,
  },
  thr: {
    key: "thr",
    slug: "thr",
    label: t("bank.politique.premiereSeuil"),
    sub: t("bank.politique.premiereSeuilSous"),
    unit: "fcfa",
    tenths: false,
    step: 500_000,
    min: 500_000,
    max: 1_000_000_000_000,
    typedMax: 1_000_000_000_000,
  },
  ageO: {
    key: "ageO",
    slug: "ageO",
    label: t("bank.politique.premiereAgeOctroi"),
    unit: "ans",
    tenths: false,
    step: 1,
    min: 1,
    max: 1_000_000,
    typedMax: 1_000_000,
  },
  ageE: {
    key: "ageE",
    slug: "ageE",
    label: t("bank.politique.premiereAgeEcheance"),
    unit: "ans",
    tenths: false,
    step: 1,
    min: 1,
    max: 1_000_000,
    typedMax: 1_000_000,
  },
  quot: {
    key: "quot",
    slug: "quot",
    label: t("bank.politique.premiereQuotite"),
    unit: "pct",
    tenths: false,
    step: 1,
    min: 1,
    max: 100,
    typedMax: 100,
  },
};

const NBSP = "\u00a0";

/** The unit printed beside an empty field, and after a committed value. */
const UNIT_WORDS: Readonly<Record<Unit, () => string>> = {
  pct: () => "%",
  point: () => t("bank.politique.premiereUnitePoint"),
  mois: () => t("bank.politique.mois"),
  fcfa: () => "FCFA",
  ans: () => t("bank.politique.ans"),
};

/** Whole tenths as the French figure: 112 is « 11,2 », 140 is « 14,0 ». */
const tenthsWords = (tenths: number): string => `${String(Math.trunc(tenths / 10))},${String(tenths % 10)}`;
/** Whole tenths as the door's dot-decimal: 112 is « 11.2 ». */
const tenthsWire = (tenths: number): string => `${String(Math.trunc(tenths / 10))}.${String(tenths % 10)}`;

const valueWords = (spec: Spec, value: number): string =>
  `${spec.tenths ? tenthsWords(value) : spec.unit === "fcfa" ? formatQuantity(value) : String(value)}${NBSP}${UNIT_WORDS[spec.unit]()}`;

/** D2 `fp-set`: « Au plus 60 mois. », « Au plus 14 %. », and the whole number or the comma figure before the unit. */
const limitWords = (spec: Spec): string => {
  const shown = spec.tenths
    ? spec.typedMax % 10 === 0
      ? String(spec.typedMax / 10)
      : tenthsWords(spec.typedMax)
    : String(spec.typedMax);
  return fill("bank.politique.premiereAuPlus", {
    max: `${shown}${NBSP}${spec.unit === "mois" ? t("bank.politique.mois") : "%"}`,
  });
};

type Read =
  | { readonly kind: "empty" }
  | { readonly kind: "value"; readonly value: number }
  | { readonly kind: "problem"; readonly words: string };

/** A typed string as the field's value: spaces of every kind are dropped, the comma is the decimal point, nothing is rounded. */
const read = (spec: Spec, typed: string): Read => {
  const bare = typed.replace(/\s/gu, "").replace(",", ".");
  if (bare === "") return { kind: "empty" };
  const notANumber: Read = { kind: "problem", words: t("bank.politique.premiereNombre") };
  let value: number;
  if (spec.tenths) {
    const [whole = "", decimal = "", ...more] = bare.split(".");
    if (more.length > 0 || !/^\d+$/u.test(whole) || (decimal !== "" && !/^\d$/u.test(decimal)))
      return notANumber;
    value = Number(whole) * 10 + Number(decimal === "" ? "0" : decimal);
  } else {
    if (!/^\d+$/u.test(bare)) return notANumber;
    value = Number(bare);
  }
  if (value <= 0) return notANumber;
  if (value > spec.typedMax) return { kind: "problem", words: limitWords(spec) };
  return { kind: "value", value };
};

/** D2 `FP_NAMES`: the eight values the counter counts, in the order the reason names them. */
const NAMES = [
  "bank.politique.premiereNomGrille",
  "bank.politique.premiereNomTolerance",
  "bank.politique.premiereNomDuree",
  "bank.politique.premiereNomTaeg",
  "bank.politique.premiereNomSeuil",
  "bank.politique.premiereNomAgeOctroi",
  "bank.politique.premiereNomAgeEcheance",
  "bank.politique.premiereNomQuotite",
] as const;

export function FirstPolicy(props: {
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onSave: (policy: FirstPolicyBody) => void;
}): React.JSX.Element {
  const [values, setValues] = useState<Readonly<Partial<Record<Key, number>>>>({});
  const [typed, setTyped] = useState<Readonly<Partial<Record<Key, string>>>>({});
  const [problems, setProblems] = useState<Readonly<Partial<Record<Key, string>>>>({});

  const done = [
    GRADES.every((grade) => values[grade] !== undefined),
    values.tol !== undefined,
    values.term !== undefined,
    values.taeg !== undefined,
    values.thr !== undefined,
    values.ageO !== undefined,
    values.ageE !== undefined,
    values.quot !== undefined,
  ];
  const count = done.filter(Boolean).length;
  const missing = NAMES.filter((_, at) => !done[at]).map((key) => t(key));
  const wrong = Object.keys(problems).length > 0;
  const reason = wrong
    ? t("bank.politique.premiereHorsLimite")
    : count === 8
      ? ""
      : fill("bank.politique.premiereRenseignez", {
          valeurs: `${missing.length > 3 ? fill("bank.politique.premiereNValeurs", { n: String(missing.length) }) : ""}${missing.slice(0, 3).join(", ")}${missing.length > 3 ? t("bank.politique.premiereEtAutres") : ""}`,
        });

  // D2: once complete, the sentence and the live button share ONE row, the button at the right edge; the incomplete states keep the stack.
  const ready = count === 8 && !wrong;
  const edit = (key: Key, next: string): void => {
    setTyped((current) => ({ ...current, [key]: next }));
    setProblems((current) => Object.fromEntries(Object.entries(current).filter(([other]) => other !== key)));
  };
  const commit = (spec: Spec): void => {
    const outcome = read(spec, typed[spec.key] ?? "");
    if (outcome.kind === "problem") {
      setProblems((current) => ({ ...current, [spec.key]: outcome.words }));
    } else if (outcome.kind === "value") {
      setValues((current) => ({ ...current, [spec.key]: outcome.value }));
    }
  };

  const save = (): void => {
    if (count !== 8 || wrong) return;
    const of = (key: Key): number => values[key] ?? 0;
    props.onSave({
      rateTolerancePct: tenthsWire(of("tol")),
      durationMaxMonths: of("term"),
      vehicleAgeMaxYears: of("ageO"),
      vehicleAgeAtMaturityMaxYears: of("ageE"),
      taegCeilingPct: tenthsWire(of("taeg")),
      delegationThresholdAmount: of("thr"),
      assignableSharePct: of("quot"),
      gradeRates: GRADES.map((grade) => ({ grade, ratePct: tenthsWire(of(grade)) })),
    });
  };

  const control = (spec: Spec): React.JSX.Element => {
    const value = values[spec.key];
    if (value !== undefined) {
      return (
        <NumberStepper
          label={spec.label}
          decreaseLabel={fill("bank.politique.baisser", { nom: spec.label })}
          increaseLabel={fill("bank.politique.monter", { nom: spec.label })}
          value={value}
          min={spec.min}
          max={spec.max}
          step={spec.step}
          format={(next) => valueWords(spec, next)}
          onChange={(next) => {
            setValues((current) => ({ ...current, [spec.key]: next }));
          }}
          testId={`bank-politique-premiere-pas-${spec.slug}`}
        />
      );
    }
    const problem = problems[spec.key];
    const id = `fp-${spec.slug}`;
    const errorId = `fe-${spec.slug}`;
    return (
      <div className="grid justify-items-end gap-1">
        <div
          className="flex items-center gap-2"
          onBlur={() => {
            commit(spec);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit(spec);
          }}
        >
          <label className="sr-only" htmlFor={id}>
            {spec.label}
          </label>
          <Input
            id={id}
            value={typed[spec.key] ?? ""}
            onChange={(next) => {
              edit(spec.key, next);
            }}
            inputMode="decimal"
            autoComplete="off"
            placeholder={t("bank.politique.premiereSaisir")}
            invalid={problem !== undefined}
            {...(problem === undefined ? {} : { describedBy: errorId })}
            className={`!w-[132px] text-right font-mono${problem === undefined ? "" : " !border-[color:var(--hk-bad)] shadow-[0_0_0_1px_var(--hk-bad)]"}`}
            testId={`bank-politique-premiere-saisie-${spec.slug}`}
          />
          <span className={`${BANK_FS_BODY} ${BANK_TEXT_2}`}>{UNIT_WORDS[spec.unit]()}</span>
        </div>
        {problem === undefined ? null : (
          <small
            id={errorId}
            data-testid={`bank-politique-premiere-erreur-${spec.slug}`}
            className={`inline-flex items-center gap-1.5 font-semibold text-[color:var(--hk-bad)] ${BANK_FS_META}`}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.2} aria-hidden="true" />
            {problem}
          </small>
        )}
      </div>
    );
  };
  const row = (key: Key): React.JSX.Element => {
    const spec = FIELDS[key];
    return (
      <RuleRow
        key={key}
        label={spec.label}
        {...(spec.sub === undefined ? {} : { sub: spec.sub })}
        control={control(spec)}
      />
    );
  };

  return (
    <div data-testid="bank-politique-premiere">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">{t("bank.politique.premiereTitre")}</h1>
        <p className="mt-1.5 text-sm text-gray-500" data-testid="bank-politique-premiere-sous-titre">
          {t("bank.politique.premiereSousTitre")}
        </p>
      </div>
      <Callout
        tone="info"
        className="mb-4"
        testId="bank-politique-premiere-aucune-valeur"
        icon={<Info className="h-5 w-5 text-indigo-700" aria-hidden="true" />}
      >
        <strong className="font-semibold">{t("bank.politique.premiereAucuneValeur")}</strong>{" "}
        {t("bank.politique.premiereAucuneValeurSuite")}
      </Callout>
      {props.refusal === null ? null : (
        <p role="alert" className="mb-4 text-sm text-rose-600" data-testid="bank-politique-refus">
          {props.refusal}
        </p>
      )}
      <div
        role="status"
        data-testid="bank-politique-premiere-compteur"
        className={`mb-3.5 flex items-center gap-3 ${BANK_FS_READ}`}
      >
        <b className="font-mono font-semibold">
          {fill("bank.politique.premiereCompteur", { n: String(count) })}
        </b>{" "}
        <span>{t("bank.politique.premiereCompteurSuite")}</span>
        <div className="w-[180px]">
          <ProgressBar label={t("bank.politique.premiereCompteurNom")} value={(count / 8) * 100} />
        </div>
      </div>
      <div className="grid grid-cols-1 items-start gap-4 min-[1100px]:grid-cols-2">
        <div className="grid gap-4">
          <PolicyCard
            testId="bank-politique-premiere-grille"
            title={t("bank.politique.premiereGrilleTitre")}
            sub={t("bank.politique.premiereGrilleSous")}
          >
            {GRADES.map((grade) => row(grade))}
            <p className={`m-0 mt-2.5 ${BANK_FS_BODY} ${BANK_TEXT_2}`}>
              {t("bank.politique.premiereGrilleAide")}
            </p>
          </PolicyCard>
          <PolicyCard
            testId="bank-politique-premiere-plafonds"
            title={t("bank.politique.premierePlafondsTitre")}
            sub={t("bank.politique.premierePlafondsSous")}
          >
            {row("tol")}
            {row("term")}
            {row("taeg")}
          </PolicyCard>
        </div>
        <div className="grid gap-4">
          <PolicyCard
            testId="bank-politique-premiere-delegation"
            title={t("bank.politique.premiereDelegationTitre")}
            sub={t("bank.politique.premiereDelegationSous")}
          >
            {row("thr")}
          </PolicyCard>
          <PolicyCard
            testId="bank-politique-premiere-vehicule"
            title={t("bank.politique.premiereVehiculeTitre")}
            sub={t("bank.politique.premiereVehiculeSous")}
          >
            {row("ageO")}
            {row("ageE")}
          </PolicyCard>
          <PolicyCard
            testId="bank-politique-premiere-emprunteur"
            title={t("bank.politique.premiereEmprunteurTitre")}
            sub={t("bank.politique.premiereEmprunteurSous")}
          >
            {row("quot")}
          </PolicyCard>
        </div>
      </div>
      <ActionBar label={t("bank.politique.barre")} testId="bank-politique-barre" className="mt-4">
        <p
          className={`m-0 ${ready ? "min-w-[16rem] flex-1" : "basis-full"} ${BANK_FS_BODY} ${BANK_TEXT_2}`}
          data-testid="bank-politique-barre-phrase"
        >
          {t("bank.politique.premierePied")}
        </p>
        {ready && !props.busy ? (
          <GuardedButton size="lg" testId="bank-politique-enregistrer" onClick={save}>
            {t("bank.politique.premiereBouton")}
          </GuardedButton>
        ) : (
          <GuardedButton
            disabled
            reason={props.busy ? t("bank.politique.enregistrement") : reason}
            {...(props.busy ? { reasonTone: "neutral" as const } : {})}
            reasonDress="chip"
            reasonIcon={<Lock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
            size="lg"
            testId="bank-politique-enregistrer"
          >
            {t("bank.politique.premiereBouton")}
          </GuardedButton>
        )}
      </ActionBar>
    </div>
  );
}
